// Square Code V2 Agent Command — Recursive Matrix (commit_3).
//
// Restyled from the SQ_RecursiveMatrix.html reference's dimension-row
// pattern (lead badge + result readout + status tag) — with THIS
// project's own real dimensions, never the prototype's fictional ones
// (PRISM/SENTINEL/ATHENA/ECHO, the readability heuristic, the meeting
// transcript). Every status TAG below is an honest, ground-truth
// classification using the required accent vocabulary (see theme.ts);
// every RESULT readout is either live data already flowing through this
// page (roster count, queue count — never re-fetched, never hardcoded)
// or a documented static fact from the real codebase (the Post Jobs
// execution path, the Engine V4 schema/function split, the Higgsfield
// provider-adapter slot). Nothing here computes or claims an analysis
// this pass cannot back with a real API response or a cited, checked
// source file.

import type { CSSProperties } from 'react'
import type { RegistryAgent } from '../../../api/client'
import type { AgentRosterState } from './AgentCommandCenter'
import type { AgentWorkspaceLoadState } from './useAgentWorkspaces'
import type { AgentActionRequestRow, QueueLoadState } from './useAgentActionQueue'
import { AGENT_CARD, AGENT_FONT, AGENT_STATUS_ACCENT, AGENT_VOID } from './theme'

export interface AgentRecursiveMatrixProps {
  rosterState: AgentRosterState
  agents: RegistryAgent[]
  queueWorkspaceState: AgentWorkspaceLoadState
  queueState: QueueLoadState
  queueRows: AgentActionRequestRow[]
}

interface Dimension {
  id: string
  lead: string
  name: string
  tagLabel: string
  tagAccent: string
  result: string
  note?: string
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

// Dimension 1 — Agent Registry. POWERED: sq_agent_registry is real and
// this page already reads it live (same endpoint Agent Control Center
// uses). The tag is fixed (the capability is real); the readout reflects
// whatever the live fetch actually returned, including its own honest
// loading/auth/forbidden/error states rather than a silent stale number.
function registryDimension(rosterState: AgentRosterState, agents: RegistryAgent[]): Dimension {
  let result: string
  switch (rosterState) {
    case 'ready':
      result = `${plural(agents.length, 'agent')} live in sq_agent_registry right now — read via api.commandCenter.agents.list(), never a fixed number.`
      break
    case 'loading':
      result = 'Loading the live count…'
      break
    case 'auth_required':
      result = 'Sign in to read the live count.'
      break
    case 'forbidden':
      result = 'Your role cannot read the live count.'
      break
    case 'error':
    default:
      result = 'Could not read the live count this time.'
      break
  }
  return {
    id: 'registry',
    lead: 'REG',
    name: 'Agent Registry',
    tagLabel: 'POWERED',
    tagAccent: AGENT_STATUS_ACCENT.green,
    result,
  }
}

// Dimension 2 — Approval Queue. POWERED: sq_agent_action_requests is
// real, and self-approval denial + the atomic pending_approval → decided
// transition are enforced server-side (approve.ts, PR #129) — not just a
// UI-side rule. The readout is the same live rows already loaded for the
// Approval Queue section above; the note states the enforcement fact,
// which does not depend on any particular load state.
function approvalQueueDimension(
  workspaceState: AgentWorkspaceLoadState,
  queueState: QueueLoadState,
  rows: AgentActionRequestRow[],
): Dimension {
  let result: string
  if (workspaceState === 'loading' || (workspaceState === 'ready' && queueState === 'loading')) {
    result = 'Loading the live queue…'
  } else if (workspaceState === 'auth_required' || queueState === 'auth_required') {
    result = 'Sign in to read the live queue.'
  } else if (workspaceState === 'forbidden' || queueState === 'forbidden') {
    result = 'Your role cannot read the live queue.'
  } else if (workspaceState === 'error' || queueState === 'error') {
    result = 'Could not read the live queue this time.'
  } else {
    result = `${plural(rows.length, 'request')} in sq_agent_action_requests right now.`
  }
  return {
    id: 'approval-queue',
    lead: 'APQ',
    name: 'Approval Queue',
    tagLabel: 'POWERED',
    tagAccent: AGENT_STATUS_ACCENT.green,
    result,
    note: 'Self-approval is denied server-side and decisions transition atomically (pending_approval → decided) — enforced in api/square/agent-workspace/approve.ts, not only hidden in this UI.',
  }
}

// Dimension 3 — Post Jobs pipeline. POWERED: confirmed in this pass by
// reading api/_lib/agent-workspace/actions.ts's executeApprovedAction —
// approving a create_post_job request inserts a real sq_post_jobs row.
// create_post_job is one of the five action types the Command Stage
// above already exposes (AGENT_ACTION_TYPES) — same real APIs, not a
// second path. There is no live endpoint on this page to read a Post
// Jobs count, so the readout states the confirmed code path honestly
// rather than inventing a number.
function postJobsDimension(): Dimension {
  return {
    id: 'post-jobs',
    lead: 'PSJ',
    name: 'Post Jobs Pipeline',
    tagLabel: 'POWERED',
    tagAccent: AGENT_STATUS_ACCENT.green,
    result:
      'Reachable through the exact command stage above: a create_post_job action request, once approved, inserts a real sq_post_jobs row (state: captured).',
    note: 'That job keeps its own review_required gate before delivery — approval here does not skip it.',
  }
}

// Dimension 4 — Engine Runtime V4. Deliberately NOT green and NOT
// "unavailable": all 8 sq_engine_* tables exist live, but its 6
// execution functions are not deployed. Deferred by operator choice.
// There is no live endpoint reporting Engine V4 function status, so this
// is asserted from the known, checked schema state (per this session's
// ground truth) — not fetched, and not implied to be a live check.
function engineV4Dimension(): Dimension {
  return {
    id: 'engine-v4',
    lead: 'ENG',
    name: 'Engine Runtime V4',
    tagLabel: 'TABLES LIVE · FUNCTIONS DEFERRED',
    tagAccent: AGENT_STATUS_ACCENT.amber,
    result: 'All 8 sq_engine_* tables exist live (worker leases, autorun, idempotency, approval-bound outbox, task memory).',
    note: 'Its 6 execution functions (register_worker, heartbeat, expire_stale_claims, try_claim_job, release_claim, next_outbox_item) are not deployed — deferred by operator choice, not broken. No live function-status endpoint exists yet; stated from known schema state, not fetched.',
  }
}

// Dimension 5 — des-01's Higgsfield provider dependency. Cyan / PROVIDER
// (provider-backed), not amber — this dimension exists to report a real,
// governed provider slot, not a block. The registry half is live (does
// des-01 actually appear in the fetched roster right now, and what is
// its real default_gate); the Higgsfield-adapter half is a documented
// static fact (api/_lib/providers/higgsfield.ts: adapter_status =
// candidate_not_runtime_verified, no HTTP client, no key handling, no
// call path). If des-01 is ever absent from the live roster, this says
// so honestly instead of assuming it is still there.
function designProviderDimension(agents: RegistryAgent[]): Dimension {
  const designAgent = agents.find((a) => a.agent_id === 'des-01') ?? null
  const result = designAgent
    ? `des-01 (${designAgent.display_name}) is live in the registry right now — default gate "${designAgent.default_gate ?? 'unset'}".`
    : 'des-01 is not present in the current live roster — this dimension only reflects a fact about it when it is.'
  return {
    id: 'design-provider',
    lead: 'DES',
    name: 'Higgsfield Provider Slot',
    tagLabel: 'PROVIDER',
    tagAccent: AGENT_STATUS_ACCENT.cyan,
    result,
    note: 'Its Higgsfield provider adapter is candidate_not_runtime_verified — no HTTP client, key handling, or call path exists yet (api/_lib/providers/higgsfield.ts, sq_provider_adapters). Behind provider_adapter_gate: may prepare a prompt/brief only, never auto-publish.',
  }
}

function DimensionRow({ d }: { d: Dimension }) {
  const badgeStyle: CSSProperties = {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center',
    fontFamily: AGENT_FONT.mono,
    fontSize: 8,
    fontWeight: 800,
    flexShrink: 0,
    border: `1px solid ${d.tagAccent}66`,
    color: d.tagAccent,
    background: `${d.tagAccent}14`,
  }
  const tagStyle: CSSProperties = {
    fontFamily: AGENT_FONT.mono,
    fontSize: 7.5,
    fontWeight: 800,
    letterSpacing: '0.05em',
    padding: '3px 8px',
    borderRadius: 5,
    border: `1px solid ${d.tagAccent}66`,
    color: d.tagAccent,
    background: `${d.tagAccent}10`,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
  }
  return (
    <div style={{ ...AGENT_CARD, display: 'flex', alignItems: 'flex-start', gap: 13, marginBottom: 8, flexWrap: 'wrap', rowGap: 8 }}>
      <span style={badgeStyle}>{d.lead}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: AGENT_FONT.display, fontWeight: 600, fontSize: 14, color: AGENT_VOID.ink }}>{d.name}</div>
        <div style={{ fontSize: 11.5, color: AGENT_VOID.muted, marginTop: 3, lineHeight: 1.5 }}>{d.result}</div>
        {d.note && <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 9, color: AGENT_VOID.faint, marginTop: 5, lineHeight: 1.6 }}>{d.note}</div>}
      </div>
      <span style={tagStyle}>{d.tagLabel}</span>
    </div>
  )
}

export default function AgentRecursiveMatrix({
  rosterState,
  agents,
  queueWorkspaceState,
  queueState,
  queueRows,
}: AgentRecursiveMatrixProps) {
  const dimensions: Dimension[] = [
    registryDimension(rosterState, agents),
    approvalQueueDimension(queueWorkspaceState, queueState, queueRows),
    postJobsDimension(),
    engineV4Dimension(),
    designProviderDimension(agents),
  ]
  return (
    <div>
      {dimensions.map((d) => (
        <DimensionRow key={d.id} d={d} />
      ))}
    </div>
  )
}
